import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Typography, Button, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { supabase } from "../supabaseClient";
import { buildHomeSnapshot } from "../utils/homeSnapshot";
import { seasonLabel } from "../utils/leagueCatalog";
import { productTypeOf } from "../utils/productLabels";
import { torontoTodayKey } from "./torontoDate";
import Carousel from "./register/Carousel";
import { FeaturedSlide, openRegistration, programInfo, useRegistrationProducts } from "./register/programs";
import TeamCrest from "./ui/TeamCrest";
import ShareButton from "./ui/ShareButton";
import MovementArrow from "./ui/MovementArrow";

const FIXTURE_COLUMNS =
  "id, date, time, field, city, division, age_group, season, home_team_id, away_team_id, home_team_score, away_team_score, is_forfeit, home_team:home_team_id (name, logo_url), away_team:away_team_id (name, logo_url)";

function useHomeSnapshot() {
  const [snapshot, setSnapshot] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      supabase.from("fixtures").select(FIXTURE_COLUMNS).eq("sport", "Soccer"),
      supabase.from("players").select("id, name, team_id, goals").eq("sport", "Soccer"),
    ])
      .then(([fx, pl]) => {
        if (cancelled) return;
        if (fx.error) throw fx.error;
        setSnapshot(buildHomeSnapshot({ fixtures: fx.data || [], players: pl.data || [], todayKey: torontoTodayKey() }));
      })
      .catch((e) => {
        console.error("Home snapshot:", e);
        if (!cancelled) setSnapshot(buildHomeSnapshot({ fixtures: [], players: [], todayKey: torontoTodayKey() }));
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return snapshot;
}

const longDate = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/Toronto" });
};

const timeLabel = (t) => {
  if (!t) return "TBD";
  const [h, m = "00"] = String(t).split(":");
  const hour = Number(h);
  return `${hour % 12 || 12}:${m.slice(0, 2)} ${hour >= 12 ? "PM" : "AM"}`;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const leagueQuery = (d, season) =>
  new URLSearchParams({ city: d.city, division: d.division, ...(season ? { season } : {}) }).toString();

const Crest = ({ team, size = 24 }) => <TeamCrest team={team} size={size} />;

// Portrait shots on phones so the photo fills the screen.
const MOBILE_HERO_PHOTOS = ["/landing/mobile-hero.webp", "/landing/division1.webp", "/landing/division2.webp", "/landing/division3.webp"];
const DESKTOP_HERO_PHOTOS = [1, 4, 6, 2, 5, 7, 3].map((n) => `/landing/splash${n}.webp`);
const HERO_INTERVAL_MS = 6000;

/** Cross-fades through match photos with a slow zoom; holds on the first one when motion is reduced. */
const HeroSlideshow = ({ photos }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    if (photos.length < 2 || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % photos.length), HERO_INTERVAL_MS);
    return () => clearInterval(id);
  }, [photos]);
  return (
    <Box aria-hidden sx={{ position: "absolute", inset: 0 }}>
      {photos.map((src, i) => {
        const n = photos.length;
        if (i !== index && i !== (index + 1) % n && i !== (index - 1 + n) % n) return null;
        return (
          <Box
            key={src}
            component="img"
            src={src}
            decoding="async"
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              opacity: i === index ? 1 : 0,
              transform: i === index ? "scale(1.06)" : "scale(1)",
              transition: `opacity 1.2s ease, transform ${HERO_INTERVAL_MS + 1200}ms linear`,
              "@media (prefers-reduced-motion: reduce)": { transition: "none", transform: "none" },
            }}
          />
        );
      })}
    </Box>
  );
};

const SectionTitle = ({ eyebrow, title, action }) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2 }}>
    <Box>
      {eyebrow && (
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
          {eyebrow}
        </Typography>
      )}
      <Typography component="h2" sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 900, letterSpacing: "-0.02em", color: "#111", lineHeight: 1.15 }}>
        {title}
      </Typography>
    </Box>
    {action}
  </Box>
);

const TextLink = ({ to, children }) => (
  <Button component={Link} to={to} size="small" sx={{ textTransform: "none", fontWeight: 800, color: "#111", whiteSpace: "nowrap" }}>
    {children}
  </Button>
);

const TeamName = ({ team, align, isNarrow }) => (
  <Box
    component={Link}
    to={`/team/${team.id}`}
    sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexDirection: align === "right" ? "row-reverse" : "row", color: "#111", textDecoration: "none", "&:hover span": { textDecoration: "underline" } }}
  >
    <Crest team={team} size={isNarrow ? 20 : 26} />
    <Typography
      component="span"
      sx={{
        fontWeight: 700,
        fontSize: { xs: "0.8rem", sm: "0.9rem" },
        lineHeight: 1.2,
        minWidth: 0,
        textAlign: align,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
      }}
    >
      {team.name || "TBD"}
    </Typography>
  </Box>
);

const GameLine = ({ f }) => {
  const isNarrow = useMediaQuery("(max-width:599px)");
  const isPlayed = f.is_forfeit || (f.home_team_score != null && f.away_team_score != null);
  const hs = Number(f.home_team_score);
  const as = Number(f.away_team_score);
  const centre = f.is_forfeit ? "FF" : isPlayed ? `${hs} – ${as}` : timeLabel(f.time);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr) 64px minmax(0,1fr)", sm: "minmax(0,1fr) 84px minmax(0,1fr)" }, alignItems: "center", gap: 1, py: 1.1, borderBottom: "1px solid #f2f2f2", "&:last-of-type": { borderBottom: 0 } }}>
      <TeamName team={{ id: f.home_team_id, ...(f.home_team || {}) }} align="right" isNarrow={isNarrow} />
      <Box sx={{ textAlign: "center" }}>
        <Typography sx={{ fontWeight: 900, fontSize: isPlayed ? "1.05rem" : "0.82rem", fontVariantNumeric: "tabular-nums", color: "#111" }}>{centre}</Typography>
        {!isPlayed && f.field && (
          <Typography noWrap sx={{ fontSize: "0.66rem", color: "#999" }}>
            {f.field}
          </Typography>
        )}
      </Box>
      <TeamName team={{ id: f.away_team_id, ...(f.away_team || {}) }} align="left" isNarrow={isNarrow} />
    </Box>
  );
};

/** Solid label so each division's block is easy to spot when scanning the page. */
const DivisionBadge = ({ city, division, size = "small" }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      bgcolor: "#111",
      color: "#fff",
      borderRadius: 1.5,
      px: size === "large" ? 1.5 : 1.1,
      py: size === "large" ? 0.6 : 0.35,
      fontWeight: 900,
      fontSize: size === "large" ? "0.95rem" : "0.78rem",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      lineHeight: 1.2,
    }}
  >
    {city && <Box component="span" sx={{ opacity: 0.65, fontWeight: 700 }}>{city}</Box>}
    {division}
  </Box>
);

const Card = ({ children, sx }) => (
  <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 3, p: { xs: 2, sm: 2.5 }, ...sx }}>{children}</Box>
);

const MatchdayCard = ({ title, day, season, snapshot }) => {
  const byDivision = new Map();
  for (const f of day.games) {
    const k = `${f.city}|${f.division}`;
    if (!byDivision.has(k)) byDivision.set(k, { city: f.city, division: f.division, games: [] });
    byDivision.get(k).games.push(f);
  }
  const multiCity = snapshot.totals.cities > 1;
  return (
    <Card>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>{title}</Typography>
      <Typography sx={{ fontWeight: 900, fontSize: "1.15rem", color: "#111", mb: 1 }}>{longDate(day.date)}</Typography>
      {[...byDivision.values()].map((g) => (
        <Box key={`${g.city}|${g.division}`} sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#f4f4f5", borderRadius: 2, pl: 0.75, pr: 0.25, py: 0.5, mb: 0.5 }}>
            <DivisionBadge city={multiCity ? g.city : null} division={g.division} />
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(0,0,0,0.55)", fontWeight: 600 }}>
              {plural(g.games.length, "game")}
            </Typography>
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextLink to={`/schedule?${leagueQuery(g, season)}&date=${day.date}`}>All games →</TextLink>
              <ShareButton
                kind="matchday"
                variant="dark"
                iconOnly
                label={`Share ${g.division} matchday`}
                sx={{ bgcolor: "#fff" }}
                data={{
                  title: title === "Latest results" ? "Results" : "Fixtures",
                  subtitle: [g.city, g.division, longDate(day.date)].join(" · "),
                  division: g.division,
                  city: g.city,
                  path: "/schedule",
                  search: `?${leagueQuery(g, season)}&date=${day.date}`,
                  games: g.games.map((f) => ({
                    home: f.home_team,
                    away: f.away_team,
                    fixture: f,
                    score: f.is_forfeit ? "FF" : f.home_team_score != null && f.away_team_score != null ? `${f.home_team_score}–${f.away_team_score}` : null,
                    time: f.time ? timeLabel(f.time) : null,
                  })),
                }}
              />
            </Box>
          </Box>
          {g.games.map((f) => (
            <GameLine key={f.id} f={f} />
          ))}
        </Box>
      ))}
    </Card>
  );
};

const TableCard = ({ d, season, multiCity }) => (
  <Card sx={{ display: "flex", flexDirection: "column" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Box component="h3" sx={{ m: 0 }}>
        <DivisionBadge city={multiCity ? d.city : null} division={d.division} size="large" />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <TextLink to={`/standings?${leagueQuery(d, season)}`}>Full table →</TextLink>
        <ShareButton
          kind="standings"
          variant="dark"
          iconOnly
          label={`Share ${d.division} table`}
          data={{
            title: "Standings",
            subtitle: [d.city, d.division, season ? seasonLabel(season) : null].filter(Boolean).join(" · "),
            path: "/standings",
            search: `?${leagueQuery(d, season)}`,
            rows: d.table,
            division: d.division,
            city: d.city,
          }}
        />
      </Box>
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) 30px 36px", gap: 1, mt: 1, pb: 0.5, borderBottom: "1px solid #f0f0f0", fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.08em", color: "#999", textTransform: "uppercase" }}>
      <span>#</span>
      <span>Club</span>
      <Box sx={{ textAlign: "center" }}>GP</Box>
      <Box sx={{ textAlign: "center" }}>Pts</Box>
    </Box>
    {d.table.slice(0, 4).map((r, i) => (
      <Box key={r.id} sx={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) 30px 36px", gap: 1, alignItems: "center", py: 0.8, borderBottom: "1px solid #f6f6f6" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: i === 0 && d.hasResults ? "#111" : "#999", minWidth: 14 }}>{i + 1}</Typography>
          <MovementArrow movement={r.movement} size={8} />
        </Box>
        <Box component={Link} to={`/team/${r.id}`} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, color: "#111", textDecoration: "none", "&:hover span": { textDecoration: "underline" } }}>
          <Crest team={r} size={22} />
          <Typography component="span" noWrap sx={{ fontWeight: 700, fontSize: "0.88rem", minWidth: 0 }}>
            {r.name}
          </Typography>
        </Box>
        <Typography sx={{ textAlign: "center", fontSize: "0.82rem", color: "#666" }}>{r.gp}</Typography>
        <Typography sx={{ textAlign: "center", fontWeight: 900, fontSize: "0.9rem" }}>{r.points}</Typography>
      </Box>
    ))}
    {d.table.length > 4 && <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.75 }}>+{d.table.length - 4} more teams</Typography>}
    <Box sx={{ mt: "auto", pt: 2 }}>
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b7860b", mb: 0.5 }}>Golden Boot</Typography>
      {d.scorers.length === 0 ? (
        <Typography sx={{ fontSize: "0.82rem", color: "#999" }}>No goals yet.</Typography>
      ) : (
        d.scorers.map((p) => (
          <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.35 }}>
            <Crest team={p.team} size={18} />
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: "0.85rem", fontWeight: 600 }}>
              {p.name}
            </Typography>
            <Typography sx={{ fontWeight: 900, color: "#b7860b", fontVariantNumeric: "tabular-nums" }}>{p.goals}</Typography>
          </Box>
        ))
      )}
    </Box>
  </Card>
);

const Landing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const snapshot = useHomeSnapshot();
  const { products, paidCountsByKey } = useRegistrationProducts();

  const openPrograms = useMemo(
    () =>
      (products || [])
        .filter((p) => productTypeOf(p) === "league" || productTypeOf(p) === "tournament")
        .map((p) => programInfo(p, paidCountsByKey))
        .filter((p) => !p.isSoldOut),
    [products, paidCountsByKey]
  );

  const heroPhotos = isMobile ? MOBILE_HERO_PHOTOS : DESKTOP_HERO_PHOTOS;
  const season = snapshot?.season;
  const totals = snapshot?.totals;
  const multiCity = (totals?.cities || 0) > 1;

  return (
    <Box sx={{ backgroundColor: "#fafafa" }}>
      <Box sx={{ position: "relative", minHeight: { xs: "min(72vh, 640px)", md: "min(78vh, 760px)" }, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", bgcolor: "#000" }}>
        <HeroSlideshow photos={heroPhotos} />
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 100%)" }} />
        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", px: 2, maxWidth: 760 }}>
          {season && (
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", fontSize: "0.78rem", mb: 1.5 }}>
              {seasonLabel(season)} season
            </Typography>
          )}
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 900, color: "#fff", textTransform: "uppercase", letterSpacing: 2, mb: 2, fontSize: { xs: "2rem", sm: "2.9rem", md: "3.4rem" }, lineHeight: 1.05 }}
          >
            HADAF Men&apos;s Soccer League
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.92)", fontWeight: 600, letterSpacing: 1, mb: 4, fontSize: { xs: "0.95rem", sm: "1.05rem" }, minHeight: "1.6em" }}>
            {totals && totals.teams > 0
              ? [plural(totals.divisions, "division"), plural(totals.teams, "team"), totals.players ? plural(totals.players, "player") : null].filter(Boolean).join(" · ")
              : "Competitive men's soccer, made accessible."}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              component={Link}
              to="/register/select-division"
              variant="contained"
              size="large"
              sx={{ backgroundColor: "#fff", color: "#000", fontWeight: 900, textTransform: "none", fontSize: "1rem", px: 3.5, py: 1.1, borderRadius: 999, boxShadow: "none", "&:hover": { backgroundColor: "#f0f0f0", boxShadow: "none" } }}
            >
              Register
            </Button>
            <Button
              component={Link}
              to="/schedule"
              variant="outlined"
              size="large"
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.7)", fontWeight: 800, textTransform: "none", fontSize: "1rem", px: 3.5, py: 1.1, borderRadius: 999, "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.1)" } }}
            >
              Fixtures
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 4, md: 6 }, display: "flex", flexDirection: "column", gap: { xs: 5, md: 7 } }}>
        {snapshot === null ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
          </Box>
        ) : (
          (snapshot.next || snapshot.latest) && (
            <Box component="section">
              <SectionTitle eyebrow="This week" title={snapshot.next ? "Next matchday" : "Latest results"} action={<TextLink to="/schedule">All fixtures →</TextLink>} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: snapshot.next && snapshot.latest ? "1fr 1fr" : "1fr" }, gap: 2, alignItems: "start" }}>
                {snapshot.next && <MatchdayCard title={snapshot.next.date === torontoTodayKey() ? "Today" : "Up next"} day={snapshot.next} season={season} snapshot={snapshot} />}
                {snapshot.latest && <MatchdayCard title="Latest results" day={snapshot.latest} season={season} snapshot={snapshot} />}
              </Box>
            </Box>
          )
        )}

        {snapshot?.divisions.length > 0 && (
          <Box component="section">
            <SectionTitle
              eyebrow={season ? seasonLabel(season) : null}
              title="Standings"
              action={<TextLink to="/insights">Insights →</TextLink>}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${Math.min(3, snapshot.divisions.length)}, 1fr)` }, gap: 2 }}>
              {snapshot.divisions.map((d) => (
                <TableCard key={`${d.city}|${d.division}`} d={d} season={season} multiCity={multiCity} />
              ))}
            </Box>
          </Box>
        )}

        {openPrograms.length > 0 && (
          <Box component="section">
            <SectionTitle eyebrow="Registration" title="Open now" action={<TextLink to="/register/select-division">All programs →</TextLink>} />
            <Carousel label="Open programs">
              {openPrograms.map((info) => (
                <FeaturedSlide key={info.product.id} info={info} onSelect={() => openRegistration(navigate, info)} />
              ))}
            </Carousel>
          </Box>
        )}

        <Box component="section" sx={{ textAlign: "center", py: 2 }}>
          <Typography sx={{ color: "rgba(0,0,0,0.55)", mb: 1 }}>More from HSL</Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              ["Rules", "/rules"],
              ["Stats", "/stats"],
              ["Youth", "/youth"],
              ["MBL", "/mbl"],
            ].map(([label, to]) => (
              <Button key={to} component={Link} to={to} variant="outlined" sx={{ textTransform: "none", fontWeight: 700, color: "#111", borderColor: "rgba(0,0,0,0.15)", borderRadius: 999 }}>
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Landing;
